/* eslint-disable node/no-unsupported-features/node-builtins */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import classNames from 'classnames';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {LoadingOverlay, PaintbrushIcon} from '@taopix/taopix-design-system';
import {LayoutSelector, UserAssetSelector} from '../../Selectors';
import {useTaopix} from '../Context/TaopixContext';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import ToolPaletteSection from '../ToolPalette/ToolPaletteSection';
import {CollectionLayout} from '../Product/LayoutSelector';
import ThemedLayoutsList from '../Theming/ThemedLayoutsList';
import useSettings from '../../Hooks/useSettings';
import {ProjectGenerator, ProjectPreviewMode, Creator} from '@taopix/taopix-core';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {ProjectPreviewState, useProjectCache} from '../Context/Projects/ProjectsContext';
import {ProductOrienation} from '../../Enums';
import {ProductLayout} from '@taopix/taopix-core/dist/types/types';
import PreviewDialog from '../Theming/PreviewDialog';
import ThemeConfirmDialog from '../Theming/ThemeConfirmDialog';
import {CreateProjectPayload} from '@taopix/taopix-core/dist/types/Generator/Creator';
import PaletteInformationMessage from './BoxSettings/PaletteInformationMessage';
import {ProjectGeneratorConfig} from '@taopix/taopix-core/dist/types/Generator/ProjectGenerator';

export type ThemesPaletteProps = {
  sectionKey: string;
};

const ThemesPalette = React.forwardRef(
  ({sectionKey}: ThemesPaletteProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    const app = useTaopix();
    const ui = useUiState();
    const settings = useSettings();
    const cache = useProjectCache();
    const isSmallScreen = ui.state.isSmallScreen;
    const {t} = useTranslation(['toolpalette', 'helptips']);
    const productLayout = useSelector(LayoutSelector);
    const assets = useSelector(UserAssetSelector);

    const [layouts, setLayouts] = useState<CollectionLayout[]>([]);
    const [serverCalled, setServerCalled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [designURL, setDesignURL] = useState('');
    const [detailsDialogState, setDetailsDialogState] = useState<ProjectPreviewState>({
      open: false,
      layout: {name: '', description: ''} as any,
      previews: [],
      info: {} as CreateProjectPayload & {extraData: string},
      dimensions: {
        height: 800,
        width: 800,
      },
    });

    const currentTheme = useMemo(() => {
      return layouts.find(
        l =>
          productLayout.collectioncode === l.collection.collectionCode &&
          productLayout.code === l.code
      );
    }, [layouts, productLayout]);

    const anchorElementRef = useRef<HTMLDivElement>(null);

    const getGeneratorSettings = useCallback(
      (layout: CollectionLayout): ProjectGeneratorConfig => {
        return {
          api: {
            ...(window as any).config,
          },
          assets: assets.map(a => a.id),
          callbackURL: settings.state.urls.callbackUrl,
          height: ui.state.isSmallScreen ? 200 : 500,
          width: ui.state.isSmallScreen ? 200 : 500,
          groupCode: settings.state.keySettings.groupCode,
          collectionCode: layout.collection.collectionCode,
          layoutCode: layout.code,
          extraData: settings.state.authentication.extraData,
          userId: settings.state.authentication.userId,
          useAssistant: 0,
          settings: {
            ...settings.state.productTheming,
            livePreviewMode: ui.state.isSmallScreen
              ? ProjectPreviewMode.List
              : settings.state.productTheming.livePreviewMode,
          },
        };
      },
      [assets, settings, ui.state.isSmallScreen]
    );

    const getDimensions = useCallback(
      (layout: ProductLayout) => {
        const dimensions = {height: 400, width: 400};
        switch (layout.orientation) {
          case ProductOrienation.Square: {
            dimensions.width = 290;
            dimensions.height = 290;
            break;
          }
          case ProductOrienation.Landscape: {
            dimensions.width = 290;
            dimensions.height = 200;
            break;
          }
          case ProductOrienation.Portrait: {
            dimensions.height = 400;
            dimensions.width = 290;
            break;
          }
        }

        return dimensions;
      },
      [detailsDialogState]
    );

    /**
     * This will either create a project or open a previewed one
     */
    const onSelectTheme = useCallback(async () => {
      const {layout, info} = detailsDialogState;

      setConfirmDialogOpen(false);

      ui.dispatch(UserInterfaceActions.setIsBusy(true));

      const projectRef = '' !== info?.productcode ? info.projectref : false;

      const config = getGeneratorSettings(layout);

      const data: CreateProjectPayload = await (projectRef
        ? Creator.openProject(config, projectRef)
        : Creator.createProject(config));

      if ('' !== data.designurl) {
        if (projectRef) {
          // since the user is selecting a previewed theme, we need to remove it
          // from the cache before unload or it will get purged
          cache.remove(projectRef);
        }

        // we need to add the current project to the cache
        // as the user has moved to a new theme
        cache
          .add({
            layout: currentTheme as CollectionLayout,
            info: {
              projectref: settings.state.project.ref,
              projectname: settings.state.project.name,
            } as CreateProjectPayload,
            previews: [],
            dimensions: {
              height: 800,
              width: 800,
            },
            open: false,
          })
          .then(() => {
            setTimeout(() => {
              // then redirect
              setDesignURL(data.designurl);
            }, 500);
          });
      } else {
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
      }
    }, [detailsDialogState]);

    const onPreviewTheme = async (theme: CollectionLayout) => {
      const history = cache.state.find(
        h => h.layout.collection.collectionCode === theme.collection.collectionCode
      );

      if (history) {
        return setDetailsDialogState({
          ...history,
          open: true,
        });
      }

      setDetailsDialogState(e => {
        return {
          ...e,
          layout: theme,
          open: true,
          previews: [],
          info: {} as CreateProjectPayload & {extraData: string},
        };
      });

      if (settings.state.productTheming.livePreviewsEnabled) {
        const generator = new ProjectGenerator(getGeneratorSettings(theme));

        const previews = await generator.load();
        const info = generator.getProjectInfo();
        const store = generator.getStore();

        const dimensions = getDimensions(store.project.productLayout as ProductLayout);

        setDetailsDialogState(current => {
          const entry = {
            ...current,
            previews,
            info,
            dimensions,
          };

          // add the project to the cache
          cache.add(entry);

          return entry;
        });
      }
    };

    const paletteOpen = ui.state.palette.section?.includes(sectionKey) || false;

    const thumbnailScrollContainerClasses = classNames([
      'flex',
      'flex-col',
      'lg:flex-1',
      'px-md',
      'pb-[40rem]',
      'overflow-y-auto',
    ]);

    const contentWrapperClasses = classNames([
      'flex',
      'lg:flex-1',
      'flex-col',
      'min-h-0',
      'sm:h-mobilePaletteContentHeight',
    ]);

    useEffect(() => {
      if (serverCalled) {
        return;
      }

      if (!paletteOpen) {
        return;
      }

      setIsLoading(true);

      app.core
        .get('server')
        .post<CollectionLayout[]>(
          `/api/product/layouts/${productLayout.code}`,
          {
            groupCode: settings.state.keySettings.groupCode,
          },
          {
            json: true,
          }
        )
        .then(({data}) => {
          setLayouts(data);
          setServerCalled(true);
        })
        .finally(() => setIsLoading(false));
    }, [paletteOpen, serverCalled, productLayout]);

    useEffect(() => {
      cache.purge();

      window.history.pushState({}, '', designURL);
    }, [designURL]);

    return (
      <ToolPaletteSection
        id={'themes-tool-palette'}
        sectionKey={sectionKey}
        useFocusMode
        title={t('str_TitleThemes', {ns: 'toolpalette'})}
        buttonIcon={<PaintbrushIcon />}
        ref={ref}
      >
        <>
          <div className={contentWrapperClasses}>
            <div
              className={thumbnailScrollContainerClasses}
              id={'themesListContainer'}
              ref={anchorElementRef}
            >
              <LoadingOverlay
                id={'themes-loading-overlay'}
                open={isLoading}
                label={t('*:str_TitlePleaseWait')}
                delay={500}
                key={'themes-loading-overlay'}
                usePortal={false}
              />

              {1 === layouts.length ? (
                <PaletteInformationMessage
                  message={t('toolpalette:str_MessageNoAvailableThemes')}
                />
              ) : (
                <ThemedLayoutsList
                  layouts={layouts}
                  columnGap={5}
                  columns={isSmallScreen ? 2 : 1}
                  onClick={(layout: CollectionLayout) => {
                    onPreviewTheme(layout);
                  }}
                />
              )}
            </div>
          </div>
          <PreviewDialog
            {...detailsDialogState}
            productTheming={settings.state.productTheming}
            onCancel={event => setDetailsDialogState({...detailsDialogState, open: false})}
            onConfirm={event => setConfirmDialogOpen(true)}
            isSmallScreen={ui.state.isSmallScreen}
          />

          <ThemeConfirmDialog
            onConfirm={event => onSelectTheme()}
            onCancel={event => setConfirmDialogOpen(false)}
            open={confirmDialogOpen}
            canConfirm={
              (settings.state.productTheming.livePreviewsEnabled &&
                0 === detailsDialogState.previews.length) ||
              false
            }
          />
        </>
      </ToolPaletteSection>
    );
  }
);

export default ThemesPalette;
